/**
 * High-speed live crypto rates service with instant prefetching,
 * multi-provider parallel race, and in-flight request deduplication.
 */

const DEFAULT_STORE_WORKER_BASE_URL = "https://psh-store-worker.neelamdevisahni521.workers.dev";
const STORE_WORKER_BASE_URL = (
  (typeof process !== "undefined" && (process.env.REACT_APP_STORE_WORKER_BASE_URL || process.env.REACT_APP_STORE_WORKER_URL)) ||
  DEFAULT_STORE_WORKER_BASE_URL
).replace(/\/+$/, "");

const RATES_ENDPOINT = `${STORE_WORKER_BASE_URL}/api/v1/rates/crypto`;

let cachedRateData = null;
let activeFetchPromise = null;
const listeners = new Set();

const fetchWithTimeout = async (url, options = {}, timeoutMs = 1800) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

const raceLiveLtcPrice = async () => {
  const now = Date.now();

  const providerCoinbase = async () => {
    try {
      const data = await fetchWithTimeout(`https://api.coinbase.com/v2/prices/LTC-USD/spot?_=${now}`, { cache: "no-store" }, 1800);
      const price = Number(data?.data?.amount);
      if (Number.isFinite(price) && price > 0) {
        return {
          ltcUsdtPrice: price,
          ltc: Number((1 / price).toFixed(6)),
          source: "coinbase"
        };
      }
    } catch {}
    throw new Error("Coinbase failed");
  };

  const providerKraken = async () => {
    try {
      const data = await fetchWithTimeout(`https://api.kraken.com/0/public/Ticker?pair=LTCUSD&_=${now}`, { cache: "no-store" }, 1800);
      const ticker = data?.result?.XLTCZUSD || data?.result?.LTCUSD;
      const price = Number(ticker?.c?.[0]);
      if (Number.isFinite(price) && price > 0) {
        return {
          ltcUsdtPrice: price,
          ltc: Number((1 / price).toFixed(6)),
          source: "kraken"
        };
      }
    } catch {}
    throw new Error("Kraken failed");
  };

  const providerOKX = async () => {
    try {
      const data = await fetchWithTimeout(`https://www.okx.com/api/v5/market/ticker?instId=LTC-USDT&_=${now}`, { cache: "no-store" }, 1800);
      const price = Number(data?.data?.[0]?.last);
      if (Number.isFinite(price) && price > 0) {
        return {
          ltcUsdtPrice: price,
          ltc: Number((1 / price).toFixed(6)),
          source: "okx"
        };
      }
    } catch {}
    throw new Error("OKX failed");
  };

  const providerKuCoin = async () => {
    try {
      const data = await fetchWithTimeout(`https://api.kucoin.com/api/v1/market/orderbook/level1?symbol=LTC-USDT&_=${now}`, { cache: "no-store" }, 1800);
      const price = Number(data?.data?.price);
      if (Number.isFinite(price) && price > 0) {
        return {
          ltcUsdtPrice: price,
          ltc: Number((1 / price).toFixed(6)),
          source: "kucoin"
        };
      }
    } catch {}
    throw new Error("KuCoin failed");
  };

  const providerWorker = async () => {
    try {
      const data = await fetchWithTimeout(`${RATES_ENDPOINT}?fresh=true&t=${now}`, {
        cache: "no-store",
        headers: { "Cache-Control": "no-cache, no-store", Pragma: "no-cache" }
      }, 1800);
      const ltcPrice = Number(data?.rates?.ltc?.usdtPrice);
      const ltcRate = Number(data?.rates?.ltc?.usdtToLtcRate);
      if (data?.success && Number.isFinite(ltcPrice) && ltcPrice > 0) {
        return {
          ltcUsdtPrice: ltcPrice,
          ltc: ltcRate || Number((1 / ltcPrice).toFixed(6)),
          source: data.source || "worker-live"
        };
      }
    } catch {}
    throw new Error("Worker rate failed");
  };

  const providerBinance = async () => {
    try {
      const data = await fetchWithTimeout(`https://api.binance.com/api/v3/ticker/price?symbol=LTCUSDT&_=${now}`, { cache: "no-store" }, 1800);
      const price = Number(data?.price);
      if (Number.isFinite(price) && price > 0) {
        return {
          ltcUsdtPrice: price,
          ltc: Number((1 / price).toFixed(6)),
          source: "binance"
        };
      }
    } catch {}
    throw new Error("Binance failed");
  };

  const providerBinanceVision = async () => {
    try {
      const data = await fetchWithTimeout(`https://data-api.binance.vision/api/v3/ticker/price?symbol=LTCUSDT&_=${now}`, { cache: "no-store" }, 1800);
      const price = Number(data?.price);
      if (Number.isFinite(price) && price > 0) {
        return {
          ltcUsdtPrice: price,
          ltc: Number((1 / price).toFixed(6)),
          source: "binance-vision"
        };
      }
    } catch {}
    throw new Error("Binance Vision failed");
  };

  // Ultra-fast parallel race across top exchanges
  try {
    const fastestResult = await Promise.any([
      providerCoinbase(),
      providerKraken(),
      providerOKX(),
      providerKuCoin(),
      providerWorker(),
      providerBinance(),
      providerBinanceVision()
    ]);

    if (fastestResult && fastestResult.ltcUsdtPrice > 0) {
      return fastestResult;
    }
  } catch {}

    // Fallback to CoinGecko
  try {
    const data = await fetchWithTimeout(`https://api.coingecko.com/api/v3/simple/price?ids=litecoin,tether&vs_currencies=usd&_=${now}`, { cache: "no-store" }, 2500);
    const ltcPrice = Number(data?.litecoin?.usd);
    const usdtPrice = Number(data?.tether?.usd || 1);
    if (Number.isFinite(ltcPrice) && ltcPrice > 0) {
      return {
        ltcUsdtPrice: ltcPrice,
        ltc: Number((usdtPrice / ltcPrice).toFixed(6)),
        source: "coingecko"
      };
    }
  } catch {}

  return null;
};

/**
 * Fetch or get live rates with automatic caching and deduplication.
 * @param {Object} options
 * @param {boolean} options.force Force un-cached refresh
 * @param {number} options.maxAgeMs Maximum cache age in milliseconds (default 60000)
 */
export const getLiveRates = async ({ force = false, maxAgeMs = 60000 } = {}) => {
  const now = Date.now();

  // Return fresh cache immediately (0 ms)
  if (!force && cachedRateData && (now - cachedRateData.fetchedAt < maxAgeMs)) {
    return cachedRateData;
  }

  // Reuse in-flight request if already fetching
  if (activeFetchPromise) {
    return activeFetchPromise;
  }

  activeFetchPromise = (async () => {
    try {
      const result = await raceLiveLtcPrice();
      if (result && result.ltcUsdtPrice > 0 && result.ltc > 0) {
        const fullData = {
          ...result,
          fetchedAt: Date.now(),
          lastUpdated: new Date().toLocaleTimeString(),
          loading: false,
          error: null
        };
        cachedRateData = fullData;
        listeners.forEach(fn => {
          try { fn(fullData); } catch {}
        });
        return fullData;
      }
      return null;
    } catch (err) {
      console.warn("[RatesService] Failed to fetch live rate:", err);
      return null;
    } finally {
      activeFetchPromise = null;
    }
  })();

  return activeFetchPromise;
};

/**
 * Prefetch live rates in the background immediately.
 * Called as soon as the cart opens or user hovers/clicks the checkout button.
 */
export const prefetchLiveRates = () => {
  const now = Date.now();
  if (cachedRateData && (now - cachedRateData.fetchedAt < 30000)) {
    return Promise.resolve(cachedRateData);
  }
  return getLiveRates({ force: true });
};

/**
 * Get current in-memory cached rate synchronously without awaiting.
 */
export const getCachedLiveRate = () => {
  if (cachedRateData && cachedRateData.ltc && cachedRateData.ltcUsdtPrice > 0 && (Date.now() - cachedRateData.fetchedAt < 60000)) {
    return cachedRateData;
  }
  return null;
};

/**
 * Subscribe to live rate updates.
 */
export const subscribeLiveRates = (callback) => {
  listeners.add(callback);
  if (cachedRateData) {
    try { callback(cachedRateData); } catch {}
  }
  return () => listeners.delete(callback);
};

const ratesService = {
  getLiveRates,
  prefetchLiveRates,
  getCachedLiveRate,
  subscribeLiveRates
};

export default ratesService;